import { Monitor, Moon, Smartphone, Sun } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { PageHeader } from '@/app/layouts/PageHeader'
import { Button } from '@/components/ui/Button'
import { SettingsSection, useTheme } from '@/features/settings'
import { useT } from '@/i18n'
import type { MessageKey } from '@/i18n'
import { cn } from '@/lib/cn'
import { promptInstall, useInstallState } from '@/lib/install'
import type { ThemePreference } from '@/lib/theme'

const options: { value: ThemePreference; label: MessageKey; icon: LucideIcon }[] = [
  { value: 'light', label: 'theme.light', icon: Sun },
  { value: 'dark', label: 'theme.dark', icon: Moon },
  { value: 'system', label: 'theme.system', icon: Monitor },
]

export function SettingsDisplayPage() {
  const t = useT()
  const { preference, setPreference } = useTheme()

  return (
    <>
      <PageHeader title={t('settings.display')} back />
      <SettingsSection title={t('theme.title')} description={t('theme.desc')}>
        <div role="radiogroup" aria-label={t('theme.title')} className="grid grid-cols-3 gap-3">
          {options.map(({ value, label, icon: Icon }) => {
            const selected = preference === value
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setPreference(value)}
                className={cn(
                  'flex flex-col items-center gap-2 rounded-md border-2 px-2 py-4 text-[15px] transition-colors',
                  selected
                    ? 'border-brand-ink bg-brand-subtle font-bold'
                    : 'border-line hover:bg-surface-hover',
                )}
              >
                <Icon className="size-6" aria-hidden />
                {t(label)}
              </button>
            )
          })}
        </div>
      </SettingsSection>
      <InstallSection />
    </>
  )
}

/** 安装到主屏幕：能弹框就给按钮，iOS 教人去分享菜单，其余告诉人去浏览器菜单里找 */
function InstallSection() {
  const t = useT()
  const state = useInstallState()

  return (
    <SettingsSection title={t('install.title')} description={t('install.desc')}>
      <div className="flex items-center gap-3 text-[15px]">
        <Smartphone className="size-5 shrink-0 text-ink-muted" aria-hidden />
        {state === 'installed' && <p>{t('install.installed')}</p>}
        {state === 'promptable' && (
          <Button onClick={() => void promptInstall()}>{t('install.button')}</Button>
        )}
        {state === 'ios' && <p className="text-ink-muted">{t('install.ios')}</p>}
        {state === 'manual' && <p className="text-ink-muted">{t('install.manual')}</p>}
      </div>
    </SettingsSection>
  )
}
